export type ApiFetch = (path: string, init?: RequestInit) => Promise<Response>;

export async function apiFetch(path: string, init?: RequestInit): Promise<Response> {
  const headers = new Headers(init?.headers);
  if (init?.body && typeof init.body === 'string' && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }
  return fetch(path, {
    ...init,
    credentials: 'include',
    headers
  });
}

export async function readJson<T>(res: Response): Promise<T> {
  const text = await res.text();
  if (!text) return {} as T;
  try {
    return JSON.parse(text) as T;
  } catch {
    return {} as T;
  }
}

/** For endpoints that must return a JSON array (`[]`). Empty body becomes `[]`. */
export async function readJsonArray<T>(res: Response): Promise<T[]> {
  const text = await res.text();
  if (!text.trim()) return [];
  let data: unknown;
  try {
    data = JSON.parse(text);
  } catch {
    throw new Error('Server returned invalid JSON.');
  }
  if (!Array.isArray(data)) {
    throw new Error(
      typeof data === 'object' && data !== null && 'error' in data
        ? String((data as { error?: unknown }).error)
        : 'Expected a JSON array from the server.'
    );
  }
  return data as T[];
}

/** Message from `{ error }`, `{ errorText }`, RFC7807 ProblemDetails (`detail` / `title`), or raw body. */
export async function readErrorMessage(res: Response): Promise<string> {
  const text = await res.text();
  if (!text.trim()) return gatewayOrStatusMessage(res.status);

  // Gateway HTML bodies (nginx/Caddy 502/503/504) — never toast raw markup.
  const looksLikeHtml = /^\s*</.test(text) || /<!DOCTYPE|<html[\s>]/i.test(text);
  if (looksLikeHtml) return gatewayOrStatusMessage(res.status);

  const parsed = parseErrorBody(text);
  if (parsed) return parsed;
  return gatewayOrStatusMessage(res.status, text);
}

function parseErrorBody(text: string): string | null {
  try {
    const data = JSON.parse(text) as Record<string, unknown>;
    const errorText = data.errorText;
    if (typeof errorText === 'string' && errorText.trim()) return errorText;
    const err = data.error;
    if (typeof err === 'string' && err.trim()) return err;
    const errors = data.errors;
    if (errors && typeof errors === 'object') {
      const parts = Object.values(errors as Record<string, unknown>)
        .flatMap((value) => (Array.isArray(value) ? value : [value]))
        .filter((value): value is string => typeof value === 'string');
      if (parts.length > 0) return parts.join(' ');
    }
    const detail = data.detail;
    if (typeof detail === 'string' && detail.trim()) return detail;
    const title = data.title;
    if (typeof title === 'string' && title.trim()) return title;
    return null;
  } catch {
    return null;
  }
}

function gatewayOrStatusMessage(status: number, fallbackText?: string): string {
  if (status === 504) return 'Request timed out. Try again.';
  if (status === 502 || status === 503) {
    return `Service temporarily unavailable (${status}). Try again.`;
  }
  if (fallbackText?.trim()) return fallbackText.slice(0, 800);
  return `Request failed (${status}).`;
}
